import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Blocks, Bug, ChevronDown, ChevronLeft, ChevronRight, CircleUser, Copy, Eye, FileText, Files, Folder, Lock, MessageSquare,
  PanelRightClose, Send, Settings, Sheet, X } from "lucide-react";
import { toast } from "sonner";
import { absoluteLink, copyText, errMsg } from "@/lib/api";
import { WorkspaceCtx, useW } from "./ctx";
import { Popover } from "./Popover";
import { DebugPanel, ExtensionsPanel, ProfilePanel, SettingsPanel, useSettings } from "./SidePanels";
import Search from "./Search";
import Stage from "./Stage";
import { SubmittalDoc, SubmittalQuick } from "./Submittal";
import { Catalog, CodeDoc, CodeQuick, CutDoc, CutQuick, InterfacesDoc, InterfacesQuick, ScheduleDoc, ScheduleQuick, SpecDoc, SpecQuick } from "./Docs";
import { ORIGIN, client, docLabel, docOf, fmtClock } from "./lib";
import "./condoor.css";

// Views render after a tab switch; wait for the element a jump targets before scrolling to it.
const waitFor = (sel, fn, tries = 30) => {
  const el = document.querySelector(sel);
  if (el) fn(el);
  else if (tries > 0) setTimeout(() => waitFor(sel, fn, tries - 1), 60);
};

export default function Workspace({ architect = false }) {
  const { pid, token } = useParams();
  const nav = useNavigate();
  const api = useMemo(() => client(architect ? token : null), [architect, token]);
  const role = architect ? "architect" : "supplier";
  const [project, setProject] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [tabs, setTabs] = useState(["submittal"]);
  const [active, setActive] = useState("submittal");
  const [modes, setModes] = useState({});
  const [pop, setPop] = useState(null);
  const [focusLine, setFocusLine] = useState(null);
  const [outline, setOutline] = useState(null);
  const [openDoors, setOpenDoors] = useState({});
  const [revealDoors, setRevealDoors] = useState(Infinity);
  const [settings, setSettings] = useSettings();
  const [chatOpen, setChatOpen] = useState(settings.chatOpen);
  // VS Code-style activity bar: which side panel is open; clicking the open one closes it.
  const [panel, setPanel] = useState("explorer");
  // File navigation history: every tab switch and jump, so back and forward retrace them.
  const [hist, setHist] = useState({ list: [{ key: "submittal", mode: "quick" }], i: 0 });
  const [now, setNow] = useState(Date.now());
  const closeT = useRef(null);
  const sawRunning = useRef(false);
  const explained = useRef(false);
  const id = architect ? token : pid;

  const refresh = useCallback(async () => {
    try {
      const p = await api.project(pid);
      setProject(p);
      if (p.run?.status === "running") sawRunning.current = true;
      if (p.run?.status === "done" && (!data || (p.explained && !explained.current))) {
        explained.current = !!p.explained;
        const r = await api.result(pid);
        setData((old) => {
          if (!old && sawRunning.current) setRevealDoors(0);
          return r;
        });
      }
    } catch (e) {
      setErr(errMsg(e));
    }
  }, [api, pid, data]);

  useEffect(() => { refresh(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const ms = project?.run?.status === "running" ? 700 : project && !project.approved_at ? 4000 : null;
    if (!ms) return undefined;
    const t = setInterval(refresh, ms);
    return () => clearInterval(t);
  }, [project?.run?.status, project?.approved_at, refresh]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  // Stream the line items in by door once the analysis finishes in front of the user (about 6 seconds).
  useEffect(() => {
    if (!data || revealDoors >= data.doors.length) return undefined;
    const step = Math.max(1, Math.ceil(data.doors.length / 20));
    const t = setTimeout(() => setRevealDoors((n) => n + step), 300);
    return () => clearTimeout(t);
  }, [data, revealDoors]);

  const derived = useMemo(() => {
    if (!data) return {};
    const drives = {};
    data.lines.forEach((l) => l.src.forEach((s) => (drives[s] = drives[s] || []).push(l)));
    const flagBySource = {};
    data.findings.forEach((f) => f.line && f.sources.forEach((s) => { flagBySource[s] = flagBySource[s] || f; }));
    return { drives, flagBySource, lineById: Object.fromEntries(data.lines.map((l) => [l.id, l])), findingById: Object.fromEntries(data.findings.map((f) => [f.id, f])),
      itemByCode: Object.fromEntries(data.items.map((i) => [i.code, i])) };
  }, [data]);

  const show = useCallback((key, mode) => {
    setTabs((t) => (t.includes(key) ? t : [...t, key]));
    setActive(key);
    if (mode) setModes((m) => ({ ...m, [key]: mode }));
  }, []);

  const openDoc = useCallback((key, mode) => {
    show(key, mode);
    setHist((h) => {
      const cur = h.list[h.i];
      const next = { key, mode: mode || modes[key] || settings.startMode };
      if (cur && cur.key === next.key && cur.mode === next.mode) return h;
      const list = [...h.list.slice(0, h.i + 1), next].slice(-50);
      return { list, i: list.length - 1 };
    });
  }, [show, modes, settings.startMode]);

  const step = (d) => setHist((h) => {
    const i = Math.max(0, Math.min(h.list.length - 1, h.i + d));
    if (i !== h.i) { show(h.list[i].key, h.list[i].mode); setPop(null); }
    return { ...h, i };
  });

  const openSource = useCallback((sid) => {
    const s = data?.sources[sid];
    if (!s) return;
    const key = docOf(s);
    if (key === "catalog" || (key?.startsWith("cut:") && !data.cut_sheets[s.sheet])) return;
    openDoc(key, "doc");
    setPop(null);
    const base = sid.split("#")[0];
    const show = (el) => {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      setTimeout(() => setPop({ kind: "source", id: el.dataset.src, el, pinned: true }), 380);
    };
    // The exact passage (a field's cell) if the view marks it, else the whole row or sheet it belongs to.
    waitFor(`[data-src="${CSS.escape(base)}"], [data-src="${CSS.escape(sid)}"]`, () =>
      show(document.querySelector(`[data-src="${CSS.escape(sid)}"]`) || document.querySelector(`[data-src="${CSS.escape(base)}"]`)));
  }, [data, openDoc]);

  const openLine = useCallback((lid, where) => {
    const l = data?.lines.find((x) => x.id === lid);
    if (!l) return;
    setPop(null);
    setOpenDoors((o) => ({ ...o, [l.door]: true }));
    setRevealDoors(Infinity);
    openDoc("submittal", where === "doc" ? "doc" : "quick");
    setFocusLine(null);
    setTimeout(() => setFocusLine(lid), 20);
    waitFor(`[data-line="${CSS.escape(lid)}"]`, (el) => el.scrollIntoView({ block: "center", behavior: "smooth" }));
  }, [data, openDoc]);

  const act = async (fn, okMsg) => {
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const ctx = {
    role, project, data, ...derived, settings, pop, focusLine, outline, openDoors, revealDoors, modes, active, tabsList: tabs,
    canBack: hist.i > 0, canForward: hist.i < hist.list.length - 1, back: () => step(-1), forward: () => step(1),
    closeTab: (key) => { setTabs((t) => t.filter((x) => x !== key)); setActive((a) => (a === key ? "submittal" : a)); setPop(null); },
    hoverPop: (p) => { clearTimeout(closeT.current); setPop((cur) => (cur?.pinned ? cur : { ...p, pinned: false })); },
    leavePop: () => { clearTimeout(closeT.current); closeT.current = setTimeout(() => setPop((cur) => (cur?.pinned ? cur : null)), 180); },
    keepPop: () => clearTimeout(closeT.current),
    pinPop: (p) => setPop((cur) => (cur?.pinned && cur.id === p.id && cur.el === p.el ? null : { ...p, pinned: true })),
    closePop: () => setPop(null),
    openDoc, openSource, openLine,
    toggleDoor: (d, v) => setOpenDoors((o) => ({ ...o, [d]: v })),
    seeInDoc: (key) => { openDoc("submittal", "doc"); setOutline(null); setTimeout(() => setOutline(key), 20);
      waitFor(`[data-sec~="${key}"]`, (el) => el.scrollIntoView({ block: "start", behavior: "smooth" })); },
    decide: (f, c) => act(() => api.decide(pid, f, c)),
    undo: (f) => act(() => api.undo(pid, f)),
    retry: () => act(async () => { sawRunning.current = true; setData(null); await api.retry(pid); }),
    review: (f, a, note) => act(() => api.review(f, a, note)),
    addLine: (door, text) => act(() => api.addLine(door, text), "Line added"),
  };

  if (err && !project) {
    return (
      <div className="cn grid min-h-screen place-items-center p-6 text-center">
        <div><p className="text-[18px] font-semibold">{err}</p>
          {!architect && <button className="btn mt-4" onClick={() => nav("/projects")}>All projects</button>}</div>
      </div>
    );
  }
  if (!project) return <div className="cn grid min-h-screen place-items-center text-[var(--muted)]">Opening the project…</div>;

  const ready = project.run.status === "done" && data;
  const elapsed = ((project.approved_at ? new Date(project.approved_at).getTime() : now) - new Date(project.created_at).getTime()) / 1000;
  const openCount = ready ? data.findings.filter((f) => f.line && !project.decisions?.[f.id]?.supplier).length : null;
  const archOpen = ready ? data.findings.filter((f) => f.line && !project.decisions?.[f.id]?.architect).length : null;
  const total = ready ? data.findings.filter((f) => f.line).length : 0;

  return (
    <WorkspaceCtx.Provider value={ctx}>
      <div className="cn flex h-screen flex-col overflow-hidden" data-testid="workspace">
        {architect && (
          <div className="flex items-center gap-3 bg-[var(--ink)] px-4 py-2 text-[13px] text-white" data-testid="invite-banner">
            <Lock size={14} /> <span>You're reviewing <b>{project.name}</b> by invite from <b>{project.supplier_name}</b>. Accept or edit each decision, add lines, then give final approval.</span>
          </div>
        )}
        <div className="flex min-h-0 flex-1">
          <ActivityBar panel={panel} setPanel={setPanel} />
          {panel === "explorer" && <Explorer onBack={() => nav("/projects")} />}
          {panel === "extensions" && <ExtensionsPanel />}
          {panel === "profile" && <ProfilePanel />}
          {panel === "settings" && <SettingsPanel settings={settings} setSettings={setSettings} />}
          {panel === "debug" && role === "supplier" && <DebugPanel />}
          <main className="flex min-w-0 flex-1 flex-col border-x border-[var(--line)] bg-[var(--bg)]">
            <TopBar ready={ready} openCount={openCount} archOpen={archOpen} api={api} refresh={refresh} elapsed={elapsed} />
            <div className="scroll min-h-0 flex-1" id="doc-scroll">
              {!ready ? <Stage /> : <View />}
            </div>
          </main>
          {chatOpen ? <Chat onClose={() => setChatOpen(false)} api={api} refresh={refresh} /> : (
            <button className="flex w-10 flex-col items-center gap-2 bg-[var(--panel)] pt-3 text-[var(--ink2)] hover:text-[var(--ink)]" onClick={() => setChatOpen(true)}
              aria-label="Open chat" data-testid="chat-open"><MessageSquare size={16} /></button>
          )}
        </div>
        <Popover />
        {project.approved_at && ready && <Final elapsed={project.elapsed_s ?? elapsed} />}
      </div>
    </WorkspaceCtx.Provider>
  );
}

function TopBar({ ready, openCount, archOpen, api, refresh, elapsed }) {
  const w = useW();
  const p = w.project;
  const mode = w.modes[w.active] || "quick";
  const hasDoc = w.active !== "catalog";
  const send = async () => {
    try {
      const r = await api.send(p.id);
      await refresh();
      await copyText(absoluteLink(r.path));
      toast.success("Sent. The architect's link is copied.");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const approve = async () => {
    try { await api.approve(); await refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const title = w.data ? docLabel(w.active, w.data) : "Submittal";
  return (
    <div className="border-b border-[var(--line)] bg-[var(--surface)]">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 border-b border-[var(--line)] px-3 py-1.5" data-testid="title-row">
        <div className="flex items-center gap-0.5">
          <button className="icon-btn" onClick={w.back} disabled={!w.canBack} aria-label="Back" data-testid="nav-back"><ChevronLeft size={16} /></button>
          <button className="icon-btn" onClick={w.forward} disabled={!w.canForward} aria-label="Forward" data-testid="nav-forward"><ChevronRight size={16} /></button>
        </div>
        <div className="flex justify-center">{w.data ? <Search /> : <div className="h-8" />}</div>
        <div className="flex items-center justify-end gap-2">
          <span className="mono tnum text-[var(--muted)]" title="Elapsed since the project was created" data-testid="elapsed">{fmtClock(elapsed)}</span>
          {ready && w.role === "supplier" && p.status === "review" && (
            <button className="btn btn-primary btn-sm h-8" disabled={openCount > 0} onClick={send} data-testid="send-to-architect"
              title={openCount ? "Decide every flag first" : ""}><Send size={13} /> Send to architect{openCount ? ` (${openCount} open)` : ""}</button>
          )}
          {ready && w.role === "supplier" && p.status !== "review" && p.invite_token && (
            <button className="btn btn-sm" onClick={async () => { await copyText(absoluteLink(`/a/${p.invite_token}`)); toast.success("Link copied"); }}
              data-testid="copy-invite"><Copy size={13} /> Architect's link</button>
          )}
          {ready && w.role === "architect" && !p.approved_at && (
            <button className="btn btn-primary btn-sm h-8" disabled={archOpen > 0} onClick={approve} data-testid="final-approval">
              Give final approval{archOpen ? ` (${archOpen} to review)` : ""}</button>
          )}
        </div>
      </div>
      <div className="scroll flex items-end gap-0.5 overflow-x-auto px-2 pt-1.5">
        {w.data ? tabList(w).map(({ key, label }) => (
          <div key={key} className={`group flex h-8 shrink-0 items-center gap-2 rounded-t-lg border border-b-0 px-3 text-[12.5px] ${w.active === key ? "border-[var(--line)] bg-[var(--bg)] font-medium" : "border-transparent text-[var(--ink2)] hover:bg-[var(--bg)]"}`}>
            <button onClick={() => w.openDoc(key)} className="max-w-[220px] truncate" data-testid={`tab-${key}`}>{label}</button>
            {key !== "submittal" && <button className="opacity-50 hover:opacity-100" onClick={() => w.closeTab?.(key)} aria-label={`Close ${label}`}><X size={12} /></button>}
          </div>
        )) : <div className="h-8 px-3 pt-2 text-[12.5px] font-medium">Submittal</div>}
      </div>
      <div className="flex items-center gap-3 border-t border-[var(--line)] bg-[var(--bg)] px-4 py-2" data-testid="doc-header">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="truncate text-[15px] font-semibold leading-tight">{title}</p>
            {ready && w.active === "submittal" && <TitleInfo />}
          </div>
          <p className="truncate text-[12px] text-[var(--muted)]">{p.supplier_name} › Division 8 › {p.name} › {crumb(w.active)}</p>
        </div>
        {ready && hasDoc && (
          <div className="flex shrink-0 rounded-full border border-[var(--line2)] bg-white p-0.5 text-[12px]" role="tablist">
            {["quick", "doc"].map((m) => (
              <button key={m} role="tab" aria-selected={mode === m} className={`rounded-full px-3 py-1 ${mode === m ? "bg-[var(--ink)] text-white" : ""}`}
                onClick={() => w.openDoc(w.active, m)} data-testid={`mode-${m}`}>{m === "quick" ? "Quick view" : "Document"}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* The submittal's title block, behind an eye button. */
function TitleInfo() {
  const w = useW();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => !ref.current?.contains(e.target) && setOpen(false);
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [open]);
  const s = w.data.summary;
  const c = w.data.checklist;
  const inc = c.filter((x) => x.status === "included").length;
  const open_ = w.data.findings.filter((f) => f.line && !(w.project.decisions?.[f.id] || {})[w.role === "supplier" ? "supplier" : "architect"]).length;
  return (
    <span ref={ref} className="relative">
      <button className={`icon-btn h-6 w-6 ${open ? "bg-black/5 text-[var(--ink)]" : ""}`} onClick={() => setOpen(!open)} aria-label="Submittal details"
        data-testid="title-info"><Eye size={14} /></button>
      {open && (
        <div className="cn-pop fade-up absolute left-0 top-8 !w-[340px] p-4 text-[13px]" data-side="below" style={{ "--ax": "8px", position: "absolute" }} data-testid="title-info-popover">
          <p className="panel-title">Submittal · generated by Condoor</p>
          <p className="mt-1 text-[17px] font-semibold">Door hardware submittal {w.project.spec_section}-001</p>
          <p className="text-[var(--ink2)]">{w.project.name} · Division 8 · Section {w.project.spec_section} Door Hardware</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {[[s.doors, "openings"], [s.lines, "line items"], [s.met, "need no decision"], [open_, "decisions open"]].map(([n, l]) => (
              <div key={l} className="rounded-lg bg-[var(--bg)] px-3 py-2"><p className="text-[18px] font-semibold tnum">{n}</p><p className="text-[12px] text-[var(--ink2)]">{l}</p></div>
            ))}
          </div>
          <p className="mt-3 text-[12.5px] text-[var(--ink2)]">Schema a–h: {inc} of {c.length} included{c.length - inc ? ` · ${c.length - inc} partial` : ""}</p>
        </div>
      )}
    </span>
  );
}

function tabList(w) {
  return [...new Set(["submittal", ...(w.tabsList || [])])].map((key) => ({ key, label: docLabel(key, w.data) }));
}

const crumb = (key) => (key === "submittal" || key.startsWith("cut:") ? (key === "submittal" ? "submittal" : "submittal › cut-sheets")
  : key.startsWith("code:") ? "public-codes" : key === "catalog" ? "catalog" : "contract-documents");

function View() {
  const w = useW();
  const k = w.active;
  const doc = (w.modes[k] || "quick") === "doc";
  if (k === "submittal") return doc ? <SubmittalDoc /> : <SubmittalQuick />;
  if (k === "spec") return doc ? <SpecDoc /> : <SpecQuick />;
  if (k === "schedule") return doc ? <ScheduleDoc /> : <ScheduleQuick />;
  if (k === "interfaces") return doc ? <InterfacesDoc /> : <InterfacesQuick />;
  if (k === "catalog") return w.role === "supplier" ? <Catalog /> : null;
  if (k.startsWith("code:")) return doc ? <CodeDoc id={k.slice(5)} /> : <CodeQuick id={k.slice(5)} />;
  if (k.startsWith("cut:")) return doc ? <CutDoc id={k.slice(4)} /> : <CutQuick id={k.slice(4)} />;
  return null;
}

function ActivityBar({ panel, setPanel }) {
  const w = useW();
  const failed = w.project.run.status === "failed";
  const Btn = ({ id, icon: I, label, dot }) => (
    <button className={`relative grid h-11 w-12 place-items-center ${panel === id ? "text-[var(--ink)]" : "text-[var(--muted)] hover:text-[var(--ink)]"}`}
      onClick={() => setPanel(panel === id ? null : id)} aria-label={label} title={label} aria-pressed={panel === id} data-testid={`activity-${id}`}>
      {panel === id && <span className="absolute left-0 top-2 h-7 w-[2px] rounded-full bg-[var(--ink)]" />}
      <I size={20} strokeWidth={1.6} />
      {dot && <span className="absolute right-2.5 top-2.5 h-2 w-2 rounded-full bg-[var(--red)]" data-testid="debug-dot" />}
    </button>
  );
  return (
    <nav className="flex w-12 shrink-0 flex-col items-center justify-between border-r border-[var(--line)] bg-[var(--panel)] py-1" data-testid="activity-bar">
      <div className="flex flex-col">
        <Btn id="explorer" icon={Files} label="Explorer" />
        <Btn id="extensions" icon={Blocks} label="Extensions" />
      </div>
      <div className="flex flex-col">
        <Btn id="profile" icon={CircleUser} label="Profile" />
        {w.role === "supplier" && <Btn id="settings" icon={Settings} label="Settings" />}
        {w.role === "supplier" && <Btn id="debug" icon={Bug} label="Debug" dot={failed} />}
      </div>
    </nav>
  );
}

function Explorer({ onBack }) {
  const w = useW();
  const [ex, setEx] = useState({ proj: true, sub: true, cuts: false, docs: true, codes: false });
  const t = (k) => setEx((e) => ({ ...e, [k]: !e[k] }));
  const p = w.project;
  const d = w.data;
  const files = p.files || [];
  const Item = ({ k, icon: I = FileText, label, meta }) => (
    <button className={`flex w-full items-center gap-2 rounded-md py-1 pl-8 pr-2 text-left text-[12.5px] ${w.active === k ? "bg-[var(--accent-bg)] font-medium text-[var(--ink)]" : "text-[var(--ink2)] hover:bg-black/5"}`}
      onClick={() => d && w.openDoc(k)} disabled={!d} data-testid={`explorer-${k}`}>
      <I size={13} className="shrink-0" /><span className="truncate">{label}</span>{meta && <span className="ml-auto shrink-0 text-[11px] text-[var(--muted)]">{meta}</span>}
    </button>
  );
  // Top-level folders carry where their files come from: a filled folder, a bold name and a dot in the origin's color.
  const FolderRow = ({ k, label, origin, count, indent = 0, top = false }) => (
    <button className={`flex w-full items-center gap-1.5 py-1 pr-3 text-left text-[12.5px] hover:bg-black/5 ${top ? "font-semibold text-[var(--ink)]" : "font-medium"}`}
      style={{ paddingLeft: 10 + indent }} onClick={() => t(k)} data-testid={`folder-${k}`}>
      {ex[k] ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
      <Folder size={13} fill={top ? "currentColor" : "none"} className={top ? "text-[var(--ink2)]" : ""} /> <span className="truncate">{label}</span>
      {count != null && <span className="text-[11px] font-normal text-[var(--muted)]">{count}</span>}
      {origin && <OriginDot origin={origin} />}
    </button>
  );
  return (
    <aside className="scroll flex w-[264px] shrink-0 flex-col bg-[var(--panel)] pb-4" data-testid="explorer">
      {w.role === "supplier" ? (
        <button className="flex items-center gap-2 px-3 py-3 text-[12.5px] font-medium text-[var(--ink2)] hover:text-[var(--ink)]" onClick={onBack} data-testid="all-projects">
          <ArrowLeft size={14} /> All projects</button>
      ) : <p className="px-3 py-3 text-[12.5px] font-medium text-[var(--ink2)]">{p.supplier_name} · shared with you</p>}
      <p className="panel-title px-3 pb-1">Explorer · this project</p>
      <FolderRow k="proj" label={p.name} />
      {ex.proj && (
        <>
          <FolderRow k="sub" label="submittal" origin="generated" indent={12} top />
          {ex.sub && <Item k="submittal" label={`SUB ${p.spec_section}-001 Submittal`} />}
          {ex.sub && d && <div style={{ paddingLeft: 14 }}><FolderRow k="cuts" label="cut-sheets" count={Object.keys(d.cut_sheets).length} indent={12} /></div>}
          {ex.sub && ex.cuts && d && Object.values(d.cut_sheets).map((c) => <div key={c.key} style={{ paddingLeft: 14 }}><Item k={`cut:${c.key}`} label={c.file} /></div>)}
          <FolderRow k="docs" label="contract-documents" origin="architect" indent={12} top />
          {ex.docs && files.map((f) => (
            <Item key={f.role} k={f.role} icon={f.role === "schedule" ? Sheet : FileText}
              label={`${f.name.split(".").pop().toUpperCase()} ${f.label} ${f.role === "spec" ? "Door Hardware" : f.role === "schedule" ? "Door Schedule" : "Interfaces"}`} />
          ))}
          {ex.docs && <p className="py-1 pl-8 text-[11.5px] text-[var(--muted)]">Floor plans · after v1</p>}
          <FolderRow k="codes" label="public-codes" origin="code" count={d?.codes?.length} indent={12} top />
          {ex.codes && d && d.codes.map((c) => <Item key={c.id} k={`code:${c.id}`} label={c.title.split(" · ")[0]} meta={c.edition.split(" ")[0]} />)}
        </>
      )}
      {w.role === "supplier" && (
        <>
          <div className="mx-3 my-2 border-t border-[var(--line2)]" />
          <button className={`flex items-center gap-1.5 py-1 pl-[10px] pr-3 text-left text-[12.5px] font-semibold hover:bg-black/5 ${w.active === "catalog" ? "bg-[var(--accent-bg)]" : ""}`}
            onClick={() => d && w.openDoc("catalog")} disabled={!d} data-testid="explorer-catalog">
            <Folder size={13} fill="currentColor" className="text-[var(--ink2)]" /> catalog · all projects <OriginDot origin="catalog" />
          </button>
        </>
      )}
    </aside>
  );
}

function OriginDot({ origin }) {
  return <span className="ml-auto h-2 w-2 shrink-0 rounded-full" style={{ background: ORIGIN[origin].color }} title={ORIGIN[origin].label}
    aria-label={ORIGIN[origin].label} data-testid={`dot-${origin}`} />;
}

function Chat({ onClose, api, refresh }) {
  const w = useW();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const box = useRef(null);
  const msgs = w.project.chat || [];
  useEffect(() => { box.current?.scrollTo({ top: box.current.scrollHeight, behavior: "smooth" }); }, [msgs.length, busy]);
  const ready = w.project.run.status === "done";
  const flagged = w.data?.findings.find((f) => f.line);
  const suggestions = ready ? [flagged && `Why is ${flagged.line} flagged?`, "Which doors are fire rated?",
    w.role === "supplier" ? "Change D-045-5 to DEMO-CL-40" : "What did the supplier change?"].filter(Boolean) : [];
  const send = async (t) => {
    const q = (t ?? text).trim();
    if (!q || busy) return;
    setBusy(true);
    setText("");
    try { await api.chat(w.project.id, q); await refresh(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <aside className="flex w-[340px] shrink-0 flex-col bg-[var(--surface)]" data-testid="chat">
      <div className="flex items-center justify-between border-b border-[var(--line)] px-4 py-2.5">
        <p className="font-semibold">Chat with Condoor</p>
        <button className="icon-btn" onClick={onClose} aria-label="Collapse chat" data-testid="chat-collapse"><PanelRightClose size={15} /></button>
      </div>
      <div ref={box} className="scroll flex-1 space-y-3 px-4 py-4 text-[13px]">
        {msgs.length === 0 && <p className="text-[var(--muted)]">Condoor reports what it finds here while it reads.</p>}
        {msgs.map((m, i) => (
          <div key={i} className={`fade-up ${m.role === "user" ? "ml-8 rounded-xl rounded-br-sm bg-[var(--ink)] px-3 py-2 text-white" : ""}`}>
            {m.role !== "user" && <p className="mb-0.5 text-[11px] font-semibold text-[var(--muted)]">{m.step ? "✓ " : ""}Condoor</p>}
            <p className="whitespace-pre-line leading-relaxed">{m.text}</p>
            {m.edit && <span className={`badge mt-1 ${m.edit.status === "refused" ? "b-conflict" : "b-edited"}`}>{m.edit.status === "refused" ? "Edit refused" : `Edited ${m.edit.line}`}</span>}
            {m.lines?.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{m.lines.slice(0, 8).map((l) => <button key={l} className="tag tag-generated" onClick={() => w.openLine(l, "quick")}>{l}</button>)}</div>}
          </div>
        ))}
        {(busy || w.project.run.status === "running") && <p className="flex items-center gap-2 text-[var(--muted)]"><span className="dot-live" /> {busy ? "Thinking…" : "Reading…"}</p>}
      </div>
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-4 pb-2">{suggestions.map((s) => <button key={s} className="btn btn-sm" onClick={() => send(s)} disabled={busy}>{s}</button>)}</div>
      )}
      <form className="flex gap-2 border-t border-[var(--line)] p-3" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder={ready ? "Ask or edit in plain words" : "Available once the submittal is built"} disabled={!ready}
          className="h-9 flex-1 rounded-full border border-[var(--line2)] px-3 outline-none focus:border-[var(--accent)]" data-testid="chat-input" />
        <button className="btn btn-dark h-9" disabled={!ready || busy || !text.trim()} data-testid="chat-send">Send</button>
      </form>
    </aside>
  );
}

function Final({ elapsed }) {
  const w = useW();
  const [hide, setHide] = useState(false);
  if (hide) return null;
  const fs = w.data.findings.filter((f) => f.line);
  const d = w.project.decisions || {};
  const edits = fs.filter((f) => d[f.id]?.architect === "edited").length + (w.project.edits || []).length;
  return (
    <div className="cn cn-overlay fixed inset-0 z-40 grid place-items-center p-4 backdrop-blur-[2px]" data-testid="final-screen">
      <div className="card fade-up w-full max-w-[620px] p-8 shadow-2xl">
        <span className="badge b-resolved">Final approval given</span>
        <h2 className="mt-3 text-[26px] font-semibold tracking-[-0.02em]">{w.project.name} is approved</h2>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[[w.data.summary.lines, "line items"], [fs.length, "decisions resolved before sending"], [edits, "edits by the architect"],
            [(w.project.arch_adds || []).length, "lines added"]].map(([n, l]) => (
            <div key={l}><p className="text-[28px] font-semibold tnum">{n}</p><p className="text-[12.5px] text-[var(--ink2)]">{l}</p></div>
          ))}
        </div>
        <div className="mt-6 rounded-xl bg-[var(--ink)] px-6 py-5 text-white">
          <p className="text-[12px] uppercase tracking-[.14em] text-white/60">Elapsed</p>
          <p className="text-[44px] font-semibold tnum leading-none" data-testid="final-elapsed">{fmtClock(elapsed)}</p>
          <p className="mt-3 text-[15px]">This process takes two weeks. You just watched it happen in minutes.</p>
        </div>
        <div className="mt-6 flex justify-end"><button className="btn" onClick={() => setHide(true)}>View the submittal</button></div>
      </div>
    </div>
  );
}
